import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Modal
 *
 * A dialog overlay for confirmations, forms, and focused interactions.
 * Animations are always on and respect prefers-reduced-motion.
 */

interface ModalContextValue {
	open: boolean
	onOpenChange: (open: boolean) => void
}

const ModalContext = React.createContext<ModalContextValue>({
	open: false,
	onOpenChange: () => {},
})

function useModalContext() {
	return React.useContext(ModalContext)
}

export interface ModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	children: React.ReactNode
	closeOnBackdropClick?: boolean
	closeOnEscape?: boolean
	className?: string
}

export function Modal({ open, onOpenChange, children, closeOnBackdropClick = true, closeOnEscape = true, className }: ModalProps) {
	const contentRef = React.useRef<HTMLDivElement>(null)

	React.useEffect(() => {
		if (!open || !closeOnEscape) return
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === 'Escape') onOpenChange(false)
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [open, closeOnEscape, onOpenChange])

	React.useEffect(() => {
		if (!open) return
		const timer = setTimeout(() => contentRef.current?.focus(), 0)
		return () => clearTimeout(timer)
	}, [open])

	React.useEffect(() => {
		if (open) {
			const prev = document.body.style.overflow
			document.body.style.overflow = 'hidden'
			return () => { document.body.style.overflow = prev }
		}
	}, [open])

	return (
		<ModalContext.Provider value={{ open, onOpenChange }}>
			<AnimatePresence>
				{open && (
					<motion.div
						className={cn('fixed inset-0 z-modal flex items-center justify-center p-4', className)}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
					>
						<motion.div
							className={cn('absolute inset-0 bg-navy-950/50 backdrop-blur-sm', closeOnBackdropClick && 'cursor-pointer')}
							aria-hidden="true"
							onClick={() => closeOnBackdropClick && onOpenChange(false)}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						/>
						<div ref={contentRef} className="relative z-10 w-full flex justify-center" tabIndex={-1}>
							{children}
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</ModalContext.Provider>
	)
}

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'
const sizeStyles: Record<ModalSize, string> = {
	sm: 'max-w-[420px]', md: 'max-w-[560px]', lg: 'max-w-[720px]', xl: 'max-w-[960px]', full: 'max-w-[calc(100vw-2rem)] h-[calc(100vh-2rem)]',
}

export interface ModalContentProps extends React.HTMLAttributes<HTMLDivElement> { size?: ModalSize }
export function ModalContent({ size = 'md', className, children }: ModalContentProps) {
	return (
		<motion.div
			role="dialog"
			aria-modal="true"
			className={cn('w-full bg-background rounded-xl border border-border shadow-xl flex flex-col max-h-[85vh] overflow-hidden', size === 'full' && 'rounded-lg max-h-none', sizeStyles[size], className)}
			initial={{ opacity: 0, scale: 0.95, y: 8 }}
			animate={{ opacity: 1, scale: 1, y: 0 }}
			exit={{ opacity: 0, scale: 0.95, y: 8 }}
			transition={{ type: 'spring', stiffness: 300, damping: 30 }}
			style={{ transformOrigin: 'center' }}
		>
			{children}
		</motion.div>
	)
}

export interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> { withDivider?: boolean }
export function ModalHeader({ withDivider = true, className, children, ...props }: ModalHeaderProps) {
	return <div className={cn('flex flex-col gap-1.5 px-6 py-5', withDivider && 'border-b border-border pb-5', className)} {...props}>{children}</div>
}
export function ModalTitle({ className, children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
	return <h2 className={cn('font-display uppercase tracking-trajan font-bold text-h3 text-navy', className)} {...props}>{children}</h2>
}
export function ModalDescription({ className, children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
	return <p className={cn('font-body text-body-sm text-muted-foreground', className)} {...props}>{children}</p>
}
export function ModalBody({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return <div className={cn('px-6 py-4 overflow-y-auto flex-1 scrollbar-warm', className)} {...props}>{children}</div>
}
export function ModalFooter({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	return <div className={cn('flex items-center justify-end gap-3 px-6 py-4 border-t border-border', className)} {...props}>{children}</div>
}

export interface ModalCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> { text?: boolean }
export function ModalClose({ text = false, className, children, ...props }: ModalCloseProps) {
	const { onOpenChange } = useModalContext()
	return (
		<button type="button" onClick={() => onOpenChange(false)} className={cn('font-ui font-semibold transition-all duration-fast ease-default', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40 focus-visible:ring-offset-1 rounded-sm', text ? 'text-navy-500 hover:text-navy-700 text-sm' : 'inline-flex items-center justify-center h-10 px-4 text-sm text-navy border border-navy rounded-md hover:bg-navy hover:text-cream-50', className)} {...props}>
			{children ?? 'Cancel'}
		</button>
	)
}
export function ModalDismiss({ className }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
	const { onOpenChange } = useModalContext()
	return (
		<motion.button type="button" onClick={() => onOpenChange(false)} aria-label="Close" className={cn('absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-md', 'text-navy-400 hover:text-navy hover:bg-cream transition-colors duration-fast', 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40', className)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
			<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
				<path d="M18 6 6 18" />
				<path d="m6 6 12 12" />
			</svg>
		</motion.button>
	)
}
